/**
 * Runtime JSON Schema validation for contract types.
 *
 * Uses Ajv to validate raw JSON objects against the contract schemas.
 * Schemas are loaded from contract/schema/ at build time (imported as JSON).
 */

// Schemas declare draft 2020-12 ($schema), so use Ajv's 2020 build — the
// default "ajv" entry only knows draft-07 and rejects the 2020-12 meta-schema.
import Ajv2020 from "ajv/dist/2020.js";
import type { ValidateFunction } from "ajv";

// Schema copies are placed here by codegen (contract/codegen/generate-ts.mjs).
import workspaceSchema from "./schemas/workspace.schema.json" with { type: "json" };
import indicatorSchema from "./schemas/indicator.schema.json" with { type: "json" };
import envelopeSchema from "./schemas/envelope.schema.json" with { type: "json" };
import themeSchema from "./schemas/theme.schema.json" with { type: "json" };
import tradingPlanSchema from "./schemas/trading-plan.schema.json" with { type: "json" };
import brandSchema from "./schemas/brand.schema.json" with { type: "json" };

import type { UserWorkspace } from "./generated/workspace.js";
import type { Indicator } from "./generated/indicator.js";
import type { ToolBridgeEnvelope } from "./generated/envelope.js";
import type { Theme } from "./generated/theme.js";
import type { Plan, TradingJournal } from "./generated/trading-plan.js";
import type { BrandProfile } from "./generated/brand.js";

// ── Ajv instance (shared, compiled once) ────────────────────────────────

const ajv = new Ajv2020({ allErrors: true, strict: false });

// ── Compiled validators ─────────────────────────────────────────────────

const _validateWorkspace: ValidateFunction = ajv.compile(workspaceSchema);
const _validateIndicator: ValidateFunction = ajv.compile(indicatorSchema);
const _validateEnvelope: ValidateFunction = ajv.compile(envelopeSchema);
const _validateTheme: ValidateFunction = ajv.compile(themeSchema);
const _validateJournal: ValidateFunction = ajv.compile(tradingPlanSchema);
const _validateBrand: ValidateFunction = ajv.compile(brandSchema);

/**
 * A single `Plan`, reached through the root's `$id`.
 *
 * The journal's root is a transfer envelope, not the thing anybody saves — the
 * store is rows. So the validator that matters is the one for one row, and Ajv
 * can hand it back by JSON pointer because the schema declares an `$id`.
 * Compiling `$defs/Plan` as a standalone schema instead would re-resolve its
 * internal `$ref`s against nothing and fail on the first `ulid`.
 */
const _validatePlan = ajv.getSchema(
  `${tradingPlanSchema.$id}#/$defs/Plan`,
) as ValidateFunction | undefined;

// ── Validation result ───────────────────────────────────────────────────

export interface ValidationResult<T> {
  valid: boolean;
  data: T | null;
  errors: string[];
}

function toResult<T>(
  validate: ValidateFunction,
  data: unknown,
): ValidationResult<T> {
  const valid = validate(data);
  if (valid) {
    return { valid: true, data: data as T, errors: [] };
  }
  const errors = (validate.errors ?? []).map(
    (e) => `${e.instancePath || "/"} ${e.message ?? "unknown error"}`,
  );
  return { valid: false, data: null, errors };
}

// ── Public API ──────────────────────────────────────────────────────────

/** Validate a raw JSON object as a UserWorkspace. */
export function validateWorkspace(data: unknown): ValidationResult<UserWorkspace> {
  return toResult<UserWorkspace>(_validateWorkspace, data);
}

/** Validate a raw JSON object as an Indicator. */
export function validateIndicator(data: unknown): ValidationResult<Indicator> {
  return toResult<Indicator>(_validateIndicator, data);
}

/** Validate a raw JSON object as a ToolBridgeEnvelope. */
export function validateEnvelope(data: unknown): ValidationResult<ToolBridgeEnvelope> {
  return toResult<ToolBridgeEnvelope>(_validateEnvelope, data);
}

/** Validate a raw JSON object as a Theme. */
export function validateTheme(data: unknown): ValidationResult<Theme> {
  return toResult<Theme>(_validateTheme, data);
}

/**
 * Validate one trading `Plan` — the row a client actually saves.
 *
 * This is the journal validator you almost always want. A plan is written once
 * and edited by hand, so a malformed one is a user's record rather than a
 * regenerable view: unlike a workspace, nothing later overwrites it.
 */
export function validatePlan(data: unknown): ValidationResult<Plan> {
  if (!_validatePlan) {
    return { valid: false, data: null, errors: ["/ Plan schema not found"] };
  }
  return toResult<Plan>(_validatePlan, data);
}

/**
 * Validate a whole `TradingJournal` — the transfer envelope, not the store.
 *
 * For an export, an import, or a shared plan arriving from elsewhere. Nothing
 * persists a document of this shape.
 */
export function validateTradingJournal(data: unknown): ValidationResult<TradingJournal> {
  return toResult<TradingJournal>(_validateJournal, data);
}

/**
 * Validate a raw JSON object as a `BrandProfile` — one file in `brands/`.
 *
 * **This is half of the check, and the half that does not need the app.** It
 * answers whether the document is well-formed: the version is one this build
 * knows, the key is a key, every feature entry has the shape of an override.
 * It cannot answer whether `features.jornal` names a real feature or whether
 * `slot: "mobile.tab"` is somewhere the screener may legally go, because the
 * list of features that exist is the registry's and the registry lives in the
 * web app — `checkProfile` in `ts/apps/web/src/lib/brand.ts` is the other half.
 *
 * Deliberately two halves rather than one: enumerating the feature ids here as
 * well would be a second copy of the registry, and the copies would drift in
 * the silent direction — the schema would go on validating a profile that names
 * a feature somebody deleted.
 */
export function validateBrand(data: unknown): ValidationResult<BrandProfile> {
  return toResult<BrandProfile>(_validateBrand, data);
}
